import { z } from 'zod/v4';

/**
 * Sort columns the admin companies list endpoint accepts. Each key maps to a
 * SQL expression in `core/db/companies/list-admin.ts` (`SORT_COLUMNS`) — keep
 * the two in sync when adding/removing columns.
 */
export const AdminCompaniesListSortColumns = [
  'updatedAt',
  'createdAt',
  'name',
  'country',
  'officialName',
  'city',
  'collaborationDateStart',
  'connectionType',
  'collaborationStatus',
  'facilityProcesses',
  'riskScore',
  'supplyChainTier',
  'collaborationEndDate',
  'responsiblePerson',
  'vendors',
  'agents',
  'allowSharingAddress',
  'allowSharingProof',
  'contactPersonEmail',
  'enabledCustomInfo',
  'internalCompanyCode',
  'notes',
  'activeUsersCount',
  'companySize',
  'companyType',
  'founded',
  'canChangeCoreInformation',
  'canReadProductStory',
  'isDemoCompany',
  'relationshipType',
] as const;

export type AdminCompaniesListSortColumn =
  (typeof AdminCompaniesListSortColumns)[number];

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
  .optional();

const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

export const AdminCompaniesListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: sortParamSchema,
  filter: z.string().max(512).optional(),
  hasFeature: z.string().min(1).optional(),
  isDropDown: booleanLike,
});

export type AdminCompaniesListQuery = z.infer<
  typeof AdminCompaniesListQuerySchema
>;

/**
 * Row shape returned by the admin companies list. `looseObject` because the
 * SQL returns ~40 columns and adding strict typing here duplicates
 * `backend/src/types/companies` without buying validation strength for an
 * admin-only read endpoint.
 *
 * Field reads in the FE (must stay populated):
 *   id, officialName, name, facilityProcesses, activeUsersCount,
 *   totalUsersCount, country, city, isDemoCompany, isClientCompany,
 *   updatedAt, byCompanyId, featuredImageFileUrl
 */
export const AdminCompanyListRowSchema = z.looseObject({
  id: z.string(),
  name: z.string().nullable().optional(),
  officialName: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
});

export type AdminCompanyListRow = z.infer<typeof AdminCompanyListRowSchema>;

// Envelope matches the legacy `reply.paginate()` shape so the FE keeps
// reading `response.data.data` / `response.data.meta` unchanged.
export const AdminCompaniesListResponseSchema = z.object({
  data: z.array(AdminCompanyListRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type AdminCompaniesListResponse = z.infer<
  typeof AdminCompaniesListResponseSchema
>;
